import type {
  Action,
  ActionExample,
  HandlerCallback,
  IAgentRuntime,
  Memory,
  State,
} from '@elizaos/core';
import { ModelType, logger, parseKeyValueXml } from '@elizaos/core';
import type { GetPortfolioContent, ZapperConfig, PortfolioData } from '../types';
import { ZapperGraphQLClient, ZapperGraphQLUtils } from '../utils/zapperGraphQL';

// ✅ ADD standardized network mapping
const NETWORK_CHAIN_MAP: { [key: string]: number } = {
  ethereum: 1,
  polygon: 137,
  arbitrum: 42161,
  optimism: 10,
  base: 8453,
  avalanche: 43114,
  bsc: 56,
  fantom: 250,
  mainnet: 1,
  eth: 1,
  matic: 137,
  arb: 42161,
  op: 10,
  avax: 43114,
  bnb: 56,
  ftm: 250,
  'binance-smart-chain': 56,
};

// ✅ REPLACE complex address extraction with this simple version:
function extractWalletAddress(text: string, state?: State): string | null {
  logger.debug('🔍 Extracting wallet address', {
    textLength: text?.length || 0,
    hasState: !!state,
  });

  if (!text) return null;

  // Simple but reliable patterns for address extraction
  const patterns = [
    // Ethereum address: 0x followed by 40 hex characters
    /0x[a-fA-F0-9]{40}/,
    // ENS name: word characters, hyphens, followed by .eth
    /\b[\w-]+\.eth\b/,
    // Basename: word characters, hyphens, followed by .base
    /\b[\w-]+\.base\b/,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      logger.info('✅ Address found', {
        address: match[0],
        pattern: pattern.source,
      });
      return match[0];
    }
  }

  logger.debug('❌ No address found in text');
  return null;
}

const getPortfolioTemplate = `Respond with an XML block containing only the extracted values. Use key-value pairs.

{{recentMessages}}

Based on the user's message, extract portfolio request details:

Extract the following information:
- address: The wallet address to check
- networks: Optional comma-separated list of networks (ethereum, polygon, arbitrum, optimism, base)

Respond with an XML block containing only the extracted values.`;

export const getPortfolioAction: Action = {
  name: 'GET_PORTFOLIO',
  similes: [
    'GET_PORTFOLIO',
    'GET_WALLET_BALANCE',
    'CHECK_PORTFOLIO',
    'VIEW_PORTFOLIO',
    'SHOW_PORTFOLIO',
    'CHECK_WALLET',
    'VIEW_WALLET',
    'SHOW_WALLET',
    'GET_BALANCE',
    'CHECK_BALANCE',
    'VIEW_BALANCE',
    'SHOW_BALANCE',
    'GET_HOLDINGS',
    'VIEW_HOLDINGS',
    'SHOW_HOLDINGS',
  ],
  description: 'Get portfolio data for a wallet address including token balances and total value',
  validate: async (runtime: IAgentRuntime, message: Memory, state?: State): Promise<boolean> => {
    logger.info('🔍 Starting GET_PORTFOLIO validation');

    // Check API key
    const apiKey = runtime.getSetting('ZAPPER_API_KEY');
    if (!apiKey) {
      logger.error('❌ ZAPPER_API_KEY not configured');
      return false;
    }

    // Check for required data (address)
    const messageText = message.content.text || '';
    const address = extractWalletAddress(messageText, state);

    if (!address) {
      logger.warn('⚠️ No wallet address found for GET_PORTFOLIO');
      return false;
    }

    logger.info('✅ Validation passed', {
      address,
      dataType: 'wallet_address',
    });

    return true;
  },
  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state?: State,
    options?: { [key: string]: unknown },
    callback?: HandlerCallback
  ): Promise<boolean> => {
    logger.info('🚀 Starting GET_PORTFOLIO handler');

    try {
      // 1. Get configuration
      const apiKey = runtime.getSetting('ZAPPER_API_KEY');
      const apiUrl = runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql';

      if (!apiKey) {
        throw new Error('ZAPPER_API_KEY not configured');
      }

      // 2. Extract required data
      const messageText = message.content.text || '';
      const address = extractWalletAddress(messageText, state);

      if (!address) {
        const helpMessage = `I need a wallet address to get portfolio data. Please provide:

• Ethereum address (e.g., 0x1234...5678)
• ENS name (e.g., vitalik.eth)  
• Basename (e.g., user.base)

Try: "Show my portfolio for 0x1234567890123456789012345678901234567890"`;

        callback?.({
          text: helpMessage,
          content: { error: 'No wallet address provided' },
        });
        return false;
      }

      logger.info('📍 Using address for lookup', { address });

      // 3. Initialize GraphQL client
      const client = new ZapperGraphQLClient({
        apiKey,
        apiUrl,
        debug: true,
        timeout: 30000,
        retryAttempts: 3,
      });

      // 4. Compose state for prompt
      let currentState: State;
      if (state) {
        currentState = state;
      } else {
        currentState = await runtime.composeState(message, ['RECENT_MESSAGES']);
      }

      // 5. Generate AI prompt for parameter extraction
      const prompt = getPortfolioTemplate.replace(
        '{{recentMessages}}',
        currentState.recentMessagesData || ''
      );

      const result = await runtime.useModel(ModelType.TEXT_SMALL, {
        prompt,
        stopSequences: [],
      });

      const content = parseKeyValueXml(result);

      // Validate the content with address fallback
      let validContent: GetPortfolioContent;
      if (isGetPortfolioContent(runtime, content)) {
        validContent = content;
      } else {
        // Fallback with extracted address
        validContent = {
          address,
          networks: 'ethereum,polygon,arbitrum,optimism,base',
        };
      }

      // 6. Process networks safely
      let networks: string[] = ['ethereum', 'polygon', 'arbitrum', 'optimism', 'base'];
      if (validContent.networks) {
        if (typeof validContent.networks === 'string') {
          networks = validContent.networks.split(',').map((n) => n.trim());
        } else if (Array.isArray(validContent.networks)) {
          networks = validContent.networks;
        }
      }

      // 7. Prepare GraphQL query and variables
      const chainIds = networks.map((network) => {
        const normalized = network.toLowerCase().trim();
        return NETWORK_CHAIN_MAP[normalized] || 1;
      });

      const query = `
        query PortfolioV2($addresses: [Address!]!, $chainIds: [Int!]) {
          portfolioV2(addresses: $addresses, chainIds: $chainIds) {
            tokenBalances {
              byToken {
                edges {
                  node {
                    balance
                    balanceUSD
                    symbol
                    name
                    network {
                      name
                    }
                    tokenAddress
                  }
                }
              }
            }
          }
        }
      `;

      const variables = {
        addresses: [validContent.address],
        chainIds: chainIds,
      };

      logger.info('📡 Making GraphQL request to Zapper API');

      // 8. Execute query using GraphQL client
      const response = await client.query(query, variables);

      if (ZapperGraphQLUtils.hasErrors(response)) {
        const errorMessage = ZapperGraphQLUtils.formatGraphQLErrors(response.errors || []);
        logger.error('❌ GraphQL errors in response', { errors: response.errors });
        throw new Error(`GraphQL errors: ${errorMessage}`);
      }

      // 9. Process response data safely
      const responseData = response.data?.portfolioV2?.tokenBalances?.byToken?.edges;

      if (!responseData || (Array.isArray(responseData) && responseData.length === 0)) {
        logger.warn('⚠️ No portfolio data found', { address });

        callback?.({
          text: `No portfolio data found for address ${validContent.address}`,
          content: {
            action: 'GET_PORTFOLIO',
            address: validContent.address,
            totalValueUSD: 0,
            tokenCount: 0,
            networks: [],
            topTokens: [],
            error: 'No portfolio data found',
          },
        });
        return true;
      }

      logger.info('✅ Data found, processing results', {
        resultCount: Array.isArray(responseData) ? responseData.length : 1,
      });

      // 10. Format the response data
      const formattedResponse = processPortfolioData(responseData, validContent.address);

      logger.info('✅ Response formatted successfully', {
        address: formattedResponse.address,
        tokenCount: formattedResponse.tokenCount,
        totalValue: formattedResponse.totalValueUSD,
      });

      const responseText = generatePortfolioResponseText(formattedResponse);

      callback?.({
        text: responseText,
        content: formattedResponse,
      });

      logger.info('✅ GET_PORTFOLIO handler completed successfully');
      return true;
    } catch (error) {
      logger.error('❌ Error in GET_PORTFOLIO handler', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      callback?.({
        text: `Unable to get portfolio data: ${errorMessage}`,
        content: { error: errorMessage },
      });

      return false;
    }
  },
  examples: [
    // Successful case with full data
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Can you show me the portfolio for 0x1234567890abcdef1234567890abcdef12345678?',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found portfolio with total value of $12,450.67 across 5 tokens',
          action: 'GET_PORTFOLIO',
        },
      },
    ],
    // ENS case
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Check wallet balance for vitalik.eth',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found wallet balance for vitalik.eth with total value of $1,234,567.89',
          action: 'GET_PORTFOLIO',
        },
      },
    ],
    // Missing data case
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Show my portfolio',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'I need a wallet address to get portfolio data. Please provide an Ethereum address, ENS name, or Basename.',
          action: 'GET_PORTFOLIO',
        },
      },
    ],
    // Specific networks
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Get my wallet balance on Ethereum and Polygon for user.base',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found portfolio on Ethereum and Polygon with total value of $5,678.90',
          action: 'GET_PORTFOLIO',
        },
      },
    ],
    // Holdings request
    [
      {
        name: '{{user1}}',
        content: {
          text: 'What are my holdings for 0xabcd...1234?',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 8 token holdings with total portfolio value of $9,876.54',
          action: 'GET_PORTFOLIO',
        },
      },
    ],
  ] as ActionExample[][],
};

// ✅ ADD proper type guard
function isGetPortfolioContent(
  _runtime: IAgentRuntime,
  content: unknown
): content is GetPortfolioContent {
  logger.debug('🔍 Starting portfolio content validation', {
    content,
    contentType: typeof content,
    isNull: content === null,
    isUndefined: content === undefined,
  });

  // Check if content is object and not null
  if (typeof content !== 'object' || content === null) {
    logger.debug('❌ Content validation failed: not an object or is null', {
      type: typeof content,
      isNull: content === null,
    });
    return false;
  }

  const contentObj = content as Record<string, unknown>;

  // Check if address exists and is a string
  if (typeof contentObj.address !== 'string') {
    logger.debug('❌ Content validation failed: address not a string', {
      hasAddress: 'address' in contentObj,
      addressType: typeof contentObj.address,
      address: contentObj.address,
    });
    return false;
  }

  // Check if address has content
  if (contentObj.address.length === 0) {
    logger.debug('❌ Content validation failed: address is empty string', {
      address: contentObj.address,
      length: contentObj.address.length,
    });
    return false;
  }

  logger.debug('✅ Content validation passed', {
    address: contentObj.address,
    addressLength: contentObj.address.length,
    hasNetworks: 'networks' in contentObj,
    networks: contentObj.networks,
  });

  return true;
}

// ✅ Safe data processing with proper types
interface TokenEdge {
  node: {
    balance: string;
    balanceUSD: number;
    symbol: string;
    name: string;
    network?: { name: string };
    tokenAddress: string;
  };
}

interface ProcessedPortfolioData {
  action: string;
  address: string;
  totalValueUSD: number;
  tokenCount: number;
  networks: string[];
  topTokens: Array<{
    symbol: string;
    name: string;
    balance: string;
    balanceUSD: number;
    network: string;
    address: string;
  }>;
}

function processPortfolioData(data: TokenEdge[], address: string): ProcessedPortfolioData {
  const tokenBalances: Array<{
    symbol: string;
    name: string;
    balance: string;
    balanceUSD: number;
    network: string;
    address: string;
  }> = [];

  let totalValueUSD = 0;
  const networksUsed = new Set<string>();

  // Process token edges safely
  for (const edge of data) {
    if (!edge?.node) continue;

    const token = edge.node;
    const networkName = token.network?.name || 'unknown';
    networksUsed.add(networkName);

    const tokenData = {
      symbol: token.symbol,
      name: token.name,
      balance: token.balance,
      balanceUSD: token.balanceUSD,
      network: networkName,
      address: token.tokenAddress,
    };

    totalValueUSD += token.balanceUSD;
    tokenBalances.push(tokenData);
  }

  // Get top 5 tokens by value
  const topTokens = tokenBalances.sort((a, b) => b.balanceUSD - a.balanceUSD).slice(0, 5);

  return {
    action: 'GET_PORTFOLIO',
    address,
    totalValueUSD,
    tokenCount: tokenBalances.length,
    networks: Array.from(networksUsed),
    topTokens,
  };
}

// ✅ Response text generation
function generatePortfolioResponseText(data: ProcessedPortfolioData): string {
  if (data.tokenCount === 0) {
    return `No portfolio data found for ${data.address}`;
  }

  let response = `Portfolio for ${data.address}:\n\n`;
  response += `💰 Total Value: $${data.totalValueUSD.toLocaleString()}\n`;
  response += `🏦 Networks: ${data.networks.join(', ')}\n`;
  response += `🪙 Total Tokens: ${data.tokenCount}\n\n`;

  if (data.topTokens.length > 0) {
    response += 'Top Holdings:\n';
    for (const token of data.topTokens) {
      const balance = Number.parseFloat(token.balance).toFixed(4);
      response += `• ${token.symbol}: ${balance} ($${token.balanceUSD.toFixed(2)})\n`;
    }
  }

  return response;
}
